import React, { useState, useEffect, useMemo } from "react";
import { PanelHeader } from "./RefreshUI";

function SeriesPopularityHeatmap({ seasonName }) {
        const [heatmapData, setHeatmapData] = useState([]);
        const [loading, setLoading] = useState(true);

        // Day labels for display
        const dayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

        // Get user's timezone offset in hours and timezone name
        const { timezoneOffset, timezoneName } = useMemo(() => {
                const offset = -new Date().getTimezoneOffset() / 60;
                const name = Intl.DateTimeFormat().resolvedOptions().timeZone;
                return { timezoneOffset: offset, timezoneName: name };
        }, []);

        useEffect(() => {
                if (!seasonName) return;

                setLoading(true);
                fetch(`https://iracing6-backend.herokuapp.com/api/series-popularity-heatmap/${encodeURIComponent(seasonName)}`)
                        .then((res) => res.json())
                        .then((data) => {
                                setHeatmapData(data);
                                setLoading(false);
                        })
                        .catch((err) => {
                                console.error("Error fetching heatmap data:", err);
                                setLoading(false);
                        });
        }, [seasonName]);

        // Convert UTC data to local timezone grid
        const localGrid = useMemo(() => {
                // Initialize 24x7 grid with zeros
                const grid = Array(7)
                        .fill(null)
                        .map(() => Array(24).fill(0));

                heatmapData.forEach((item) => {
                        // Convert UTC hour to local hour
                        let localHour = item.hour + timezoneOffset;
                        let localDay = item.day - 1; // Convert 1-7 to 0-6

                        // Handle day overflow/underflow
                        if (localHour >= 24) {
                                localHour -= 24;
                                localDay = (localDay + 1) % 7;
                        } else if (localHour < 0) {
                                localHour += 24;
                                localDay = (localDay - 1 + 7) % 7;
                        }

                        grid[localDay][Math.floor(localHour)] += item.totalParticipants;
                });

                return grid;
        }, [heatmapData, timezoneOffset]);

        // Calculate min/max for color scaling
        const { minVal, maxVal } = useMemo(() => {
                const allValues = localGrid.flat();
                const nonZeroValues = allValues.filter((v) => v > 0);
                return {
                        minVal: nonZeroValues.length > 0 ? Math.min(...nonZeroValues) : 0,
                        maxVal: Math.max(...allValues),
                };
        }, [localGrid]);

        // Generate color based on value (dark to bright yellow)
        const getColor = (value) => {
                if (value === 0) return "rgba(255, 255, 255, 0.04)"; // Faint cell for no data

                const ratio = (value - minVal) / (maxVal - minVal || 1);
                // Interpolate from dark (#3d3200) to bright yellow (#f1c40f)
                const r = Math.round(61 + ratio * (241 - 61));
                const g = Math.round(50 + ratio * (196 - 50));
                const b = Math.round(0 + ratio * (15 - 0));
                return `rgb(${r}, ${g}, ${b})`;
        };

        // Format hour for display
        const formatHour = (hour) => {
                if (hour === 0) return "12a";
                if (hour === 12) return "12p";
                if (hour < 12) return `${hour}a`;
                return `${hour - 12}p`;
        };

        let body;
        if (loading) {
                body = <p className="hp-panel__note">Loading race times…</p>;
        } else if (heatmapData.length === 0) {
                body = <p className="hp-panel__note">No session data for this series yet.</p>;
        } else {
                body = (
                        <>
                                <div className="pg-heat" role="table" aria-label="Participants by day and hour">
                                        <span className="pg-heat__corner" />
                                        {Array(24)
                                                .fill(null)
                                                .map((_, hour) => (
                                                        <span key={`h${hour}`} className="pg-heat__hour">
                                                                {hour % 3 === 0 ? formatHour(hour) : ""}
                                                        </span>
                                                ))}
                                        {dayLabels.map((dayLabel, dayIndex) => (
                                                <React.Fragment key={dayLabel}>
                                                        <span className="pg-heat__day">{dayLabel}</span>
                                                        {localGrid[dayIndex].map((value, hour) => (
                                                                <span
                                                                        key={hour}
                                                                        className="pg-heat__cell"
                                                                        style={{ backgroundColor: getColor(value) }}
                                                                        title={`${dayLabel} ${formatHour(hour)}: ${value} participants`}
                                                                />
                                                        ))}
                                                </React.Fragment>
                                        ))}
                                </div>
                                <div className="pg-heat__legend">
                                        <span className="pg-heat__watermark">iracingdata.com</span>
                                        Fewer
                                        <span className="pg-heat__ramp" aria-hidden="true" />
                                        More
                                </div>
                        </>
                );
        }

        return (
                <section className="hp-panel">
                        <PanelHeader title="Popular race times" />
                        <p className="hp-panel__note">Participants by day and hour, in your time zone ({timezoneName}).</p>
                        {body}
                </section>
        );
}

export default SeriesPopularityHeatmap;
